import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Separator } from "@/shared/components/ui/separator";
import {
  Settings as SettingsIcon,
  Shield,
  Bell,
  Globe,
  Database,
  Zap,
  Clock,
  Save,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

export default function Settings() {
  const [saving, setSaving] = useState(false);
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    // General
    platformName: "PayGate Thailand",
    timezone: "Asia/Bangkok",
    defaultCurrency: "THB",
    
    // Security
    requireIPWhitelist: true,
    hmacAlgorithm: "sha256",
    signatureHeaderName: "X-Signature",
    timestampTolerance: 300,
    
    // Callbacks
    callbackRetryEnabled: true,
    callbackMaxRetries: 5,
    callbackRetryDelay: [5, 30, 120, 600, 3600],
    callbackTimeout: 30,
    
    // Webhooks
    webhookIPValidation: true,
    webhookSignatureValidation: true,
    webhookDuplicateCheck: true,
    
    // QR
    qrDefaultExpiry: 15,
    qrMaxAmount: 2000000,
    qrMinAmount: 20,
    
    // Routing
    routingHealthCheckInterval: 60,
    routingFailoverThreshold: 3,
    routingAutoDisableOnFail: true,
    
    // Notifications
    emailNotifications: true,
    slackNotifications: false,
    alertThreshold: 10000,
  });

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    // Simulate save
    await new Promise((r) => setTimeout(r, 1000));
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settingsTitle")}</h1>
          <p className="text-slate-500">{t("platformConfig")}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? t("saving") : t("saveChanges")}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-white p-1 border">
          <TabsTrigger value="general" className="gap-2">
            <SettingsIcon className="h-4 w-4" />
            {t("general")}
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" />
            {t("security")}
          </TabsTrigger>
          <TabsTrigger value="callbacks" className="gap-2">
            <Zap className="h-4 w-4" />
            {t("callbacks")}
          </TabsTrigger>
          <TabsTrigger value="qr" className="gap-2">
            <Globe className="h-4 w-4" />
            {t("qrSettings")}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />
            {t("notificationsSettings")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("generalSettings")}</CardTitle>
                <CardDescription>{t("basicPlatformConfig")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t("platformName")}</Label>
                    <Input
                      value={settings.platformName}
                      onChange={(e) => handleChange("platformName", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("timezone")}</Label>
                    <Select
                      value={settings.timezone}
                      onValueChange={(v) => handleChange("timezone", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Asia/Bangkok">Asia/Bangkok (ICT)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("defaultCurrency")}</Label>
                  <Select
                    value={settings.defaultCurrency}
                    onValueChange={(v) => handleChange("defaultCurrency", v)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="THB">THB - Thai Baht</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="security">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("securitySettings")}</CardTitle>
                <CardDescription>{t("apiSecurityConfig")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("requireIpWhitelist")}</Label>
                    <p className="text-sm text-slate-500">{t("acceptApiCallsFromWhitelistedIps")}</p>
                  </div>
                  <Switch
                    checked={settings.requireIPWhitelist}
                    onCheckedChange={(v) => handleChange("requireIPWhitelist", v)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t("hmacAlgorithm")}</Label>
                    <Select
                      value={settings.hmacAlgorithm}
                      onValueChange={(v) => handleChange("hmacAlgorithm", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sha256">SHA-256</SelectItem>
                        <SelectItem value="sha512">SHA-512</SelectItem>
                        <SelectItem value="sha1">SHA-1 (legacy)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("signatureHeaderName")}</Label>
                    <Input
                      value={settings.signatureHeaderName}
                      onChange={(e) => handleChange("signatureHeaderName", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("timestampTolerance")}</Label>
                  <Input
                    type="number"
                    value={settings.timestampTolerance}
                    onChange={(e) => handleChange("timestampTolerance", parseInt(e.target.value))}
                  />
                  <p className="text-xs text-slate-500">
                    {t("rejectOldRequests")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="callbacks">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("callbackSettings")}</CardTitle>
                <CardDescription>{t("configureCallback")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("enableCallbackRetry")}</Label>
                    <p className="text-sm text-slate-500">{t("autoRetryFailedCallbacks")}</p>
                  </div>
                  <Switch
                    checked={settings.callbackRetryEnabled}
                    onCheckedChange={(v) => handleChange("callbackRetryEnabled", v)}
                  />
                </div>

                {settings.callbackRetryEnabled && (
                  <>
                    <div className="space-y-2">
                      <Label>{t("maxRetryAttempts")}</Label>
                      <Input
                        type="number"
                        value={settings.callbackMaxRetries}
                        onChange={(e) => handleChange("callbackMaxRetries", parseInt(e.target.value))}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t("retryDelaySchedule")}</Label>
                      <Input
                        value={settings.callbackRetryDelay.join(", ")}
                        onChange={(e) =>
                          handleChange(
                            "callbackRetryDelay",
                            e.target.value.split(",").map((v) => parseInt(v.trim()))
                          )
                        }
                      />
                      <p className="text-xs text-slate-500">
                        Comma-separated delay values: 5s, 30s, 2min, 10min, 1hr
                      </p>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>{t("callbackTimeout")}</Label>
                  <Input
                    type="number"
                    value={settings.callbackTimeout}
                    onChange={(e) => handleChange("callbackTimeout", parseInt(e.target.value))}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="qr">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("qrPaymentSettings")}</CardTitle>
                <CardDescription>{t("promptPayConfig")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>{t("defaultQrExpiry")}</Label>
                  <Input
                    type="number"
                    value={settings.qrDefaultExpiry}
                    onChange={(e) => handleChange("qrDefaultExpiry", parseInt(e.target.value))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t("minimumAmount")}</Label>
                    <Input
                      type="number"
                      value={settings.qrMinAmount}
                      onChange={(e) => handleChange("qrMinAmount", parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("maximumAmount")}</Label>
                    <Input
                      type="number"
                      value={settings.qrMaxAmount}
                      onChange={(e) => handleChange("qrMaxAmount", parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex gap-3">
                    <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900">{t("supportedBanks")}</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        SCB, KBank, BBL, Krungsri, TTB - {t("enableInProviderSettings")}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="notifications">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("notificationSettings")}</CardTitle>
                <CardDescription>{t("configureAlerts")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("emailNotifications")}</Label>
                    <p className="text-sm text-slate-500">{t("sendAlertsViaEmail")}</p>
                  </div>
                  <Switch
                    checked={settings.emailNotifications}
                    onCheckedChange={(v) => handleChange("emailNotifications", v)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("slackNotifications")}</Label>
                    <p className="text-sm text-slate-500">{t("sendAlertsToSlack")}</p>
                  </div>
                  <Switch
                    checked={settings.slackNotifications}
                    onCheckedChange={(v) => handleChange("slackNotifications", v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("alertThreshold")}</Label>
                  <Input
                    type="number"
                    value={settings.alertThreshold}
                    onChange={(e) => handleChange("alertThreshold", parseInt(e.target.value))}
                  />
                  <p className="text-xs text-slate-500">
                    {t("sendAlertAboveAmount")}
                  </p>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-900">{t("alertTypes")}</h4>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1">
                        <li>• {t("highValueTransactions")}</li>
                        <li>• {t("providerFailures")}</li>
                        <li>• {t("failedCallbackRetries")}</li>
                        <li>• {t("suspiciousActivity")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}